import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { FaArrowLeft, FaCheckCircle, FaUsers, FaLock } from "react-icons/fa";
import { authFetch } from "../../utils/api/authFetch";
import { ContentThumb, TeacherAvatar, TagBadges, typeMeta, formatCount, SegmentViewer } from "../../components/marketplace";
import { ProtectedArea } from "../../components/protected";

// The "watch" view for one content item: header + teacher/subscribe row, then
// the lesson's ordered segments (text and/or media). The first segment is a free
// preview; the rest are locked for non-subscribers, with a subscribe CTA. "More
// from this teacher" sits in the sidebar.

const Watch = () => {
    const navigate = useNavigate();
    const { bundleId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [subscribing, setSubscribing] = useState(false);
    const [notFound, setNotFound] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await authFetch(`/api/marketplace/content/${bundleId}`);
            if (res.status === 404) { setNotFound(true); return; }
            const d = await res.json().catch(() => ({}));
            setData(d.data || null);
        } catch (err) {
            toast.error("Could not load this content");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bundleId]);

    const handleSubscribe = async () => {
        if (!data?.teacher) return;
        setSubscribing(true);
        try {
            const res = await authFetch(`/api/marketplace/subscribe/${data.teacher.id}`, { method: "POST" });
            if (res.status === 401 || res.status === 403) throw new Error("Your session expired. Please log in again.");
            const d = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(d.message || "Failed to start subscription");
            window.location.href = d.payment_url;
        } catch (err) {
            toast.error(err.message);
            setSubscribing(false);
        }
    };

    if (loading) return <Shell onBack={() => navigate("/marketplace")}><p className="text-gray-500 p-6">Loading…</p></Shell>;
    if (notFound || !data) {
        return (
            <Shell onBack={() => navigate("/marketplace")}>
                <div className="text-center py-24">
                    <h2 className="text-lg font-semibold text-gray-800">Content not found</h2>
                    <p className="text-sm text-gray-500 mt-1">It may have been removed or unpublished.</p>
                    <button onClick={() => navigate("/marketplace")} className="mt-4 bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-semibold">Back to marketplace</button>
                </div>
            </Shell>
        );
    }

    const {
        title, description, body, type, thumbnail_url, subjects = [], classes = [], locked,
        segments = [], teacher, price, subscribed, subscriberCount, canSubscribe, more = [],
    } = data;
    const meta = typeMeta(type);
    const goChannel = () => teacher && navigate(`/marketplace/teacher/${teacher.id}`);

    return (
        <Shell onBack={() => navigate("/marketplace")}>
            <div className="max-w-6xl mx-auto p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main column */}
                <div className="lg:col-span-2 min-w-0 space-y-4">
                    {/* Cover hero */}
                    <div className="rounded-xl overflow-hidden">
                        <ContentThumb type={type} thumbnailUrl={thumbnail_url} title={title} rounded="rounded-xl" />
                    </div>

                    {/* Title + meta */}
                    <div>
                        <h1 className="text-xl font-bold text-gray-900">{title}</h1>
                        <p className="text-sm text-gray-500 mt-1">{meta.label} · {segments.length} sub-topic{segments.length === 1 ? "" : "s"}</p>
                        <TagBadges subjects={subjects} classes={classes} className="mt-2" />
                    </div>

                    {/* Teacher row */}
                    <div className="flex items-center justify-between gap-3 py-4 border-y border-gray-100">
                        <button onClick={goChannel} className="flex items-center gap-3 text-left min-w-0">
                            <TeacherAvatar name={teacher?.name} src={teacher?.profile_image} size={44} />
                            <div className="min-w-0">
                                <p className="font-semibold text-gray-900 truncate">{teacher?.name}</p>
                                <p className="text-xs text-gray-500 flex items-center gap-1.5">
                                    <FaUsers className="text-gray-400" /> {formatCount(subscriberCount)} subscriber{subscriberCount === 1 ? "" : "s"}
                                </p>
                            </div>
                        </button>
                        {subscribed ? (
                            <span className="inline-flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 px-4 py-2 rounded-full font-semibold text-sm shrink-0">
                                <FaCheckCircle /> Subscribed
                            </span>
                        ) : (
                            canSubscribe && (
                                <button onClick={handleSubscribe} disabled={subscribing}
                                    className="bg-blue-600 text-white px-5 py-2 rounded-full font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 shrink-0">
                                    {subscribing ? "…" : `Subscribe${price != null ? ` · ₦${Number(price).toLocaleString()}/mo` : ""}`}
                                </button>
                            )
                        )}
                    </div>

                    {/* Description + intro */}
                    {description && (
                        <div className="bg-gray-100 rounded-xl p-4 text-sm text-gray-700 whitespace-pre-wrap">{description}</div>
                    )}
                    {body && (
                        <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{body}</div>
                    )}

                    {locked && (
                        <div className="bg-blue-50 border border-blue-100 text-blue-800 text-sm rounded-lg p-3 flex items-center gap-2">
                            <FaLock className="shrink-0" /> The first sub-topic is a free preview. Subscribe to unlock the rest.
                        </div>
                    )}

                    {/* Segments — read-only: watermarked, no download, no copy/print.
                        See components/protected for exactly what that covers. */}
                    <ProtectedArea contentClassName="space-y-4">
                        {segments.length === 0 && <p className="text-sm text-gray-400">No sub-topics in this content yet.</p>}
                        {segments.map((seg, idx) => (
                            <SegmentViewer
                                key={seg.id}
                                segment={seg}
                                index={idx}
                                onSubscribe={handleSubscribe}
                                canSubscribe={canSubscribe}
                                subscribing={subscribing}
                                price={price}
                                teacherName={teacher?.name}
                            />
                        ))}
                    </ProtectedArea>
                </div>

                {/* Sidebar: more from this teacher */}
                <div className="min-w-0">
                    <h2 className="text-sm font-bold text-gray-800 mb-3">More from {teacher?.name?.split(" ")[0] || "this teacher"}</h2>
                    {more.length === 0 ? (
                        <p className="text-sm text-gray-400">Nothing else yet.</p>
                    ) : (
                        <div className="space-y-3">
                            {more.map((m) => (
                                <button key={m.id} onClick={() => navigate(`/marketplace/watch/${m.id}`)} className="flex gap-3 w-full text-left group">
                                    <div className="w-40 shrink-0">
                                        <ContentThumb type={m.type} thumbnailUrl={m.thumbnail_url} title={m.title} rounded="rounded-lg" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug group-hover:text-blue-600">{m.title}</p>
                                        <TagBadges subjects={m.subjects} classes={m.classes} max={2} className="mt-1" />
                                        <p className="text-xs text-gray-400 mt-1">{typeMeta(m.type).label}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Shell>
    );
};

const Shell = ({ children, onBack }) => (
    <div className="min-h-screen bg-gray-50 font-montserrat">
        <nav className="bg-[#193FAE] shadow-md px-4 sm:px-6 py-3 flex items-center gap-3 sticky top-0 z-20">
            <button onClick={onBack} className="text-white" aria-label="Back"><FaArrowLeft size={18} /></button>
            <h1 className="text-lg font-bold text-white">Watch</h1>
        </nav>
        {children}
    </div>
);

export default Watch;
